import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import {
  BODY,
  BODY_SMALL,
  HEADING_2,
} from "@/components/marketing/marketingType";
import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import type { LandingCta } from "@/content/landing/types";
import { usePageIdentity } from "../LandingPageContext";
import {
  LandingButton,
  LandingSection,
  LinkedText,
} from "../LandingPrimitives";
import CompareGrid from "@/components/marketing/CompareGrid";
import { CONTAIN_GAP, FLOW_MEASURE, flowSection } from "./landingRun";
import MatrixGraded from "./MatrixGraded";
import MatrixTable from "./MatrixTable";

type Matrix = Laid<"matrix">;

/**
 * `container_new.padding-bottom`: the grid variant's own rhythm, which breaks
 * shorter than the shared one at the wide step and the same below it.
 */
/** `.eth-table-body_wrap`: the footnotes side by side, 16 apart, each on half the row. */
const NOTES_ROW = {
  display: "flex",
  alignItems: "flex-start",
  gap: "16px",
  width: 1,
  [MID]: { flexDirection: "column" },
} satisfies SxProps<Theme>;

/** `.body-small_100`: 18 on 150%, 16 below the wide step. */
const NOTE_ROW_LINE = {
  width: 1,
  color: marketingPalette.ink,
  fontSize: 18,
  lineHeight: "27px",
  [NOT_WIDE]: { fontSize: 16, lineHeight: "24px" },
} satisfies SxProps<Theme>;

const GRID_PADDING = {
  pb: "48px",
  [NOT_WIDE]: { pb: "64px" },
  [TINY]: { pb: "48px" },
};

/** `reliable-title_wrap` / `benefit_title_wrap`: the heading and its lead, led 8 apart. */
const HEADING_WRAP = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  alignItems: "center",
  textAlign: "center",
  mx: "auto",
};

/** The measure the versus heading is set to, which is what wraps its lead to two lines. */
const HEADING_MEASURE = { maxWidth: "592px" };

/**
 * The published versus lead links the site's own name, and it is that anchor,
 * not a weight, that sets "Webshare" in the page's ink rather than the muted
 * grey around it. Each page's anchor is its own: most open a new tab, and the
 * Decodo page names the bare host in the same tab.
 */
const SELF_LINK: LandingCta = {
  label: "Webshare",
  href: "https://www.webshare.io/",
  newTab: true,
};
const SELF_LINKS: Readonly<Record<string, LandingCta>> = {
  "/decodo-alternative": { label: "Webshare", href: "https://webshare.io/" },
};

/** The versus heading breaks after its colon; the published copy carries no space there. */
function TitleLines({ title }: { title: string }) {
  const at = title.indexOf(":");
  if (at < 0) {
    return <>{title}</>;
  }
  return (
    <>
      {title.slice(0, at + 1)}
      <br />
      {title.slice(at + 1)}
    </>
  );
}

/** This page's own anchor for the site's name. */
const selfLink = (path: string): LandingCta => SELF_LINKS[path] ?? SELF_LINK;

/** The heading, the versus one broken after its colon. */
function MatrixTitle({ title, versus }: { title?: string; versus: boolean }) {
  if (!title) {
    return null;
  }
  return (
    <Typography component="h2" sx={HEADING_2}>
      {versus ? <TitleLines title={title} /> : title}
    </Typography>
  );
}

/** The line under the heading; the versus one links the site's own name. */
function MatrixLead({ block, versus }: { block: Matrix; versus: boolean }) {
  const { path } = usePageIdentity();
  if (!block.body) {
    return null;
  }
  return (
    <Typography sx={block.bodyTone === "small" ? BODY_SMALL : BODY}>
      <LinkedText
        text={block.body}
        links={versus ? [selfLink(path)] : undefined}
      />
    </Typography>
  );
}

/** The section's heading and the line under it, centred over the table. */
function MatrixHeading({ block, versus }: { block: Matrix; versus: boolean }) {
  if (!block.title && !block.body) {
    return null;
  }
  return (
    <Box sx={[HEADING_WRAP, versus ? HEADING_MEASURE : {}]}>
      <MatrixTitle title={block.title} versus={versus} />
      <MatrixLead block={block} versus={versus} />
    </Box>
  );
}

/**
 * The rows as the grid's columns. Rows with a button spend the first column
 * naming the axis; rows without one compare products head to head, so every
 * column is an attribute.
 */
function gridColumns(block: Matrix, axis: boolean) {
  return block.rows.map((row) => ({
    name: row.label ?? "",
    icon: row.icon ?? row.logo,
    current: row.current,
    values: axis ? row.cells.slice(0, block.columns.length - 1) : row.cells,
    cta: row.cta,
  }));
}

/** `side-compare-cta`: the panel turns into a column below the wide step, and stretches below the card one. */
const FOOT_PANEL = {
  alignItems: "center",
  // Not `wrap`: the published panel squeezes the button rather than dropping
  // it under the notes, which is a row taller.
  flexWrap: "nowrap",
  rowGap: "8px",
  // The published row spaces its two children apart rather than gapping them.
  columnGap: 0,
  // A flex item cannot shrink under its content unless it is told it may, and
  // the published panel holds the notes to their measure and squeezes the
  // button instead.
  "& > a, & > button": { minWidth: 0 },
  p: "16px",
  borderRadius: "16px",
  backgroundColor: marketingPalette.surface,
  [NOT_WIDE]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "16px",
  },
  // `.button_new.is_hug` keeps its own width at the card step, where the
  // other controls fill their column; the panel stretches it on a phone.
  [MID]: { "& > a, & > button": { width: "auto" } },
  [TINY]: { alignItems: "stretch" },
} as const;

/** The notes and the button under the table; `side-compare-cta` sets the versus table's on a tinted panel. */
function MatrixFoot({ block, versus }: { block: Matrix; versus: boolean }) {
  return (
    <Box
      sx={[
        {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: "24px",
          flexWrap: "wrap",
        },
        versus ? FOOT_PANEL : {},
      ]}
    >
      <Box
        sx={
          block.notesRow
            ? NOTES_ROW
            : {
                display: "flex",
                flexDirection: "column",
                gap: versus ? "16px" : "8px",
                maxWidth: versus ? "700px" : "720px",
                // The published notes keep their measure whatever the button needs.
                ...(versus ? { flexShrink: 0 } : {}),
              }
        }
      >
        {block.notes?.map((note) => (
          <Typography
            key={note}
            sx={block.notesRow ? NOTE_ROW_LINE : BODY_SMALL}
          >
            {note}
          </Typography>
        ))}
      </Box>
      {block.cta ? <LandingButton cta={block.cta} /> : null}
    </Box>
  );
}

/** The section the matrix stands in: the flowing column where it is contained, the grid's own rhythm otherwise. */
function matrixSection(block: Matrix, versus: boolean) {
  if (block.contain) {
    return flowSection(block);
  }
  // `.container_new.is-top_padding`: the section's break over it and none under.
  if (block.pad === "top") {
    return { padding: "top" as const, sx: {} };
  }
  return {
    padding: versus ? ("both" as const) : ("none" as const),
    sx: versus || block.pad === "none" ? {} : GRID_PADDING,
  };
}

/** The table itself, in the shape the block names. */
function MatrixBody({ block, versus }: { block: Matrix; versus: boolean }) {
  const axis = block.rows.some((row) => row.cta);
  if (versus) {
    return <MatrixTable columns={block.columns} rows={block.rows} />;
  }
  if (block.graded) {
    return <MatrixGraded columns={block.columns} rows={block.rows} />;
  }
  return (
    <CompareGrid
      variant={axis ? "prem" : "competitors"}
      label={axis ? block.columns[0] : ""}
      attributes={axis ? block.columns.slice(1) : block.columns}
      columns={gridColumns(block, axis)}
      markerFirst={block.markerFirst}
    />
  );
}

export default function MatrixBlock({ block }: { block: Matrix }) {
  const versus = block.variant === "versus";
  return (
    <LandingSection {...matrixSection(block, versus)}>
      <Box
        sx={[
          {
            display: "flex",
            flexDirection: "column",
            gap: "32px",
            // `why-web_item`: the versus section closes the gap a step earlier.
            ...(versus ? { [MID]: { gap: "24px" } } : {}),
          },
          block.contain ? [FLOW_MEASURE, CONTAIN_GAP] : null,
        ].flat()}
      >
        <MatrixHeading block={block} versus={versus} />
        <MatrixBody block={block} versus={versus} />
        {block.notes?.length || block.cta ? (
          <MatrixFoot block={block} versus={versus} />
        ) : null}
      </Box>
    </LandingSection>
  );
}
